import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';

interface LiquidFillChartProps {
  percentage: number;
  size?: number;
  color?: string;
  label?: string;
  sublabel?: string;
  thickness?: number;
}

const LiquidFillChart: React.FC<LiquidFillChartProps> = ({
  percentage,
  size = 200,
  color = '#2563EB',
  label = '',
  sublabel = '',
  thickness = 8,
}) => {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedPercentage(percentage);
    }, 100);
    return () => clearTimeout(timer);
  }, [percentage]);

  // Calculate wave parameters
  const waveHeight = 8;
  const waveCount = 3;
  const fillHeight = (100 - animatedPercentage) / 100;

  // Create wave path
  const createWavePath = (offset: number) => {
    const points: string[] = [];
    const steps = 50;

    for (let i = 0; i <= steps; i++) {
      const x = (i / steps) * size;
      const angle = ((i / steps) * waveCount * 2 * Math.PI) + offset;
      const y = (fillHeight * size) + Math.sin(angle) * waveHeight;
      points.push(`${x},${y}`);
    }

    return `M 0,${size} L 0,${points[0].split(',')[1]} L ${points.join(' L ')} L ${size},${size} Z`;
  };

  return (
    <div className="relative inline-flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <defs>
          {/* Gradient for the ring - vibrant with shine */}
          <linearGradient id={`ring-gradient-${label}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.9" />
            <stop offset="50%" stopColor={color} stopOpacity="1" />
            <stop offset="100%" stopColor={color} stopOpacity="0.85" />
          </linearGradient>

          {/* Water gradient - very light and translucent */}
          <linearGradient id={`water-gradient-${label}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.15" />
            <stop offset="50%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.35" />
          </linearGradient>

          {/* Wave gradient - very light for wave effect */}
          <linearGradient id={`wave-gradient-${label}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={color} stopOpacity="0.3" />
          </linearGradient>

          {/* Clip path for circular mask */}
          <clipPath id={`circle-clip-${label}`}>
            <circle cx={size / 2} cy={size / 2} r={radius - thickness / 2} />
          </clipPath>

          {/* Glow filter */}
          <filter id={`glow-${label}`}>
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Shine effect filter */}
          <filter id={`shine-${label}`}>
            <feGaussianBlur in="SourceAlpha" stdDeviation="4" />
            <feOffset dx="0" dy="0" result="offsetblur" />
            <feFlood floodColor="#ffffff" floodOpacity="0.5" />
            <feComposite in2="offsetblur" operator="in" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background circle - light with good contrast */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#F3F4F6"
          strokeWidth={thickness}
        />

        {/* Background progress ring - same percentage as water with higher opacity */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeOpacity="0.4"
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - (animatedPercentage / 100) * circumference }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />

        {/* Inner white circle for glass effect - MUST be before water */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius - thickness / 2}
          fill="white"
          fillOpacity="1"
        />

        {/* Water fill with realistic waves */}
        <g clipPath={`url(#circle-clip-${label})`} transform="rotate(90)" style={{ transformOrigin: 'center' }}>
          {/* Base water fill - solid light color */}
          <motion.rect
            x="0"
            y={fillHeight * size}
            width={size}
            height={size - (fillHeight * size)}
            fill={color}
            fillOpacity="0.3"
            initial={{ y: size }}
            animate={{ y: fillHeight * size }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
          />

          {/* Wave 1 - Primary wave with lighter color */}
          <motion.path
            d={createWavePath(0)}
            fill={color}
            fillOpacity="0.35"
            initial={{ d: createWavePath(0) }}
            animate={{ d: createWavePath(Math.PI * 2) }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear',
            }}
          />

          {/* Wave 2 - Secondary wave for depth */}
          <motion.path
            d={createWavePath(Math.PI)}
            fill={color}
            fillOpacity="0.2"
            initial={{ d: createWavePath(Math.PI) }}
            animate={{ d: createWavePath(Math.PI * 3) }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'linear',
            }}
          />

          {/* Shimmer effect on water surface */}
          <motion.ellipse
            cx={size / 2}
            cy={fillHeight * size}
            rx={size * 0.3}
            ry={4}
            fill="white"
            fillOpacity="0.3"
            animate={{
              opacity: [0.2, 0.4, 0.2],
              rx: [size * 0.25, size * 0.35, size * 0.25],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />

          {/* Bubbles - more subtle */}
          {[...Array(6)].map((_, i) => (
            <motion.circle
              key={i}
              cx={size * (0.15 + i * 0.14)}
              cy={size}
              r={1.5 + Math.random() * 2}
              fill="white"
              fillOpacity="0.6"
              initial={{ cy: size, opacity: 0 }}
              animate={{
                cy: fillHeight * size - 15,
                opacity: [0, 0.6, 0],
              }}
              transition={{
                duration: 2.5 + Math.random() * 1.5,
                repeat: Infinity,
                delay: i * 0.5,
                ease: 'easeOut',
              }}
            />
          ))}

          {/* Light reflection on water */}
          <motion.path
            d={`M ${size * 0.3},${fillHeight * size + 10} Q ${size * 0.5},${fillHeight * size + 5} ${size * 0.7},${fillHeight * size + 10}`}
            stroke="white"
            strokeWidth="2"
            fill="none"
            strokeOpacity="0.4"
            strokeLinecap="round"
            animate={{
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </g>

        {/* Progress ring with glow - rendered AFTER water so it's on top */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#ring-gradient-${label})`}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - (animatedPercentage / 100) * circumference }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          filter={`url(#glow-${label})`}
        />
      </svg>

      {/* Center text - always dark for better readability on light water */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div
          className="text-3xl font-bold transition-colors duration-500"
          style={{
            color: color,
            textShadow: '0 2px 4px rgba(255,255,255,0.8)',
            fontWeight: 700,
          }}
        >
          <CountUp end={percentage} duration={1.5} suffix="%" />
        </div>
        {label && (
          <div
            className="text-sm font-semibold mt-1 transition-colors duration-500"
            style={{
              color: '#374151',
              textShadow: '0 1px 2px rgba(255,255,255,0.8)'
            }}
          >
            {label}
          </div>
        )}
        {sublabel && (
          <div
            className="text-xs font-medium transition-colors duration-500"
            style={{
              color: '#6B7280',
              textShadow: '0 1px 2px rgba(255,255,255,0.6)'
            }}
          >
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiquidFillChart;

